import { ShoppingBag } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import type { Product } from "@/hooks/use-shop-data";

interface SearchResultItemProps {
  product: Product;
  onSelect: () => void;
}

export function SearchResultItem({ product, onSelect }: SearchResultItemProps) {
  const formattedPrice = `£${product.price.toFixed(2)}`;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <Link
      className="group mx-2 my-1 flex cursor-pointer items-center gap-4 rounded-lg p-4 transition-all duration-200 hover:scale-[1.02] hover:bg-accent/50"
      href={`/products/${product.handle}`}
      onClick={onSelect}
      role="menuitem"
    >
      <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-muted shadow-sm">
        {product.images?.[0] && !imageError ? (
          <>
            {/* Placeholder while image loads */}
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted/80">
                <ShoppingBag className="h-6 w-6 animate-pulse text-muted-foreground" />
              </div>
            )}
            <Image
              alt={product.name}
              className={`object-cover transition-all duration-300 group-hover:scale-105 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
              fill
              loading="lazy"
              onError={() => setImageError(true)}
              onLoad={() => setImageLoaded(true)}
              priority={false}
              sizes="48px"
              // Optimize loading for search results
              src={product.images[0].url}
            />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-muted/80">
            <ShoppingBag className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-bold font-heading text-foreground text-sm">
          {product.name}
        </div>
        <div className="font-normal text-muted-foreground text-optimal text-sm">
          {formattedPrice}
        </div>
      </div>
    </Link>
  );
}
